import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // WCAG 1.4.13 / 2.1.1: Escape dismisses an open mega menu and returns focus to its
    // top-level link, instead of leaving focus inside a panel that is being hidden.
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.#state.activeItem) return;
        const item = this.#state.activeItem;
        const trigger = item.matches('a, button') ? item : item.querySelector('.menu-list__link, a, button');
        this.#debouncedActivateHandler.cancel();
        this.#deactivate(item);
        if (trigger instanceof HTMLElement) {
          this.#suppressFocusActivation = true;
          trigger.focus();
        }
      },
      { signal: this.#abortController.signal }
    );

    // Mega menu photos wait for intent instead of loading with the page (see snippets/alt-menu-layout-image.liquid).
    // pointerover and focusin both run before the item's pointerenter / focus handler opens its panel.
    this.addEventListener('pointerover', this.#warmImages, { signal: this.#abortController.signal, passive: true });
    this.addEventListener('focusin', this.#warmImages, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  /** Set while focus is being returned to a trigger after Escape, so it does not reopen. */
  #suppressFocusActivation = false;

  activate = (event) => {
    if (this.#suppressFocusActivation && event?.type === 'focus') {
      this.#suppressFocusActivation = false;
      return;
    }
    this.#suppressFocusActivation = false;
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.dataset.menuInteraction = 'active';

    const header = this.closest('.header');
    if (header) {
      header.classList.add('header--menu-active');
    }

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.dataset.menuInteraction = 'idle';

    const header = this.closest('.header');
    if (header) {
      header.classList.remove('header--menu-active');
    }
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /** True once every menu photo has been requested. */
  #allImagesWarmed = false;

  /** True once the idle load of the other panels' photos is queued. */
  #otherImagesQueued = false;

  /**
   * Loads the mega menu photos on intent. Replaces Horizon's preload, which fetched every panel's photos on
   * every page load. The photos of the item under the pointer or keyboard focus start at high priority, right
   * away, as its panel starts to reveal; the other panels' photos follow when the browser is idle, so panels opened
   * after the first are normally ready before they are hovered.
   * @param {Event} event
   */
  #warmImages = (event) => {
    if (this.#allImagesWarmed) return;

    const item = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;

    // "More" opens the overflow panel, which can show any of the overflowing items: load them all now.
    if (item instanceof HTMLElement && item.slot === 'more') {
      loadMenuImages(this);
      this.#allImagesWarmed = true;
      return;
    }

    if (item) loadMenuImages(item, 'high');

    if (this.#otherImagesQueued) return;
    this.#otherImagesQueued = true;
    requestIdleCallback(
      () => {
        loadMenuImages(this);
        this.#allImagesWarmed = true;
      },
      { timeout: 1500 }
    );
  };
}

/**
 * Starts loading the menu photos inside an element: deferred ones (data-src / data-srcset) get their real URLs,
 * lazy ones are switched to eager.
 * @param {Element} root
 * @param {'high' | 'low' | 'auto'} [priority]
 */
function loadMenuImages(root, priority) {
  for (const image of root.querySelectorAll('img[data-src], img[data-srcset], img[loading="lazy"]')) {
    if (!(image instanceof HTMLImageElement)) continue;

    const { src, srcset } = image.dataset;
    if (priority) image.fetchPriority = priority;
    image.loading = 'eager';
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
